import type { CanvasItem, ChatSelectionWidget, Message } from '~/components/chat/message'
import type { ProgressMessageData, StreamingMessage } from '~/contexts/GlobalStreamingContext'

/** Parses message ID to extract database message ID. Formats: "inline-19" -> "19", "user-123" -> "123" */
export function parseMessageId(messageId: string): string | null {
  const match = messageId.match(/^(?:inline|api|user)-(\d+)(?:-\d+)?$/)
  return match ? match[1] : null
}

/** Creates optimistic user message for immediate display (userName + createdTime). */
export function createOptimisticUserMessage(params: {
  messageId: string
  content: string
  userName?: string | null
  contextUpload?: Array<{
    file_upload?: { file_name: string; s3_path: string }
    directory_upload?: { directory_name: string; s3_path: string }
  }>
  createdAt?: string
}): StreamingMessage {
  return {
    id: params.messageId,
    role: 'user',
    type: 'text',
    content: params.content.trim(),
    contextUpload: params.contextUpload,
    userName: params.userName || undefined,
    createdTime: params.createdAt ?? new Date().toISOString()
  }
}

function normalizeSelectionWidgetFields(
  fields: Record<string, unknown> | undefined,
  fallbackLabel = ''
): ChatSelectionWidget | null {
  if (!fields) return null

  const rawOptions = Array.isArray(fields.options) ? fields.options : []
  const options = rawOptions
    .map(option => {
      if (!option || typeof option !== 'object') return null
      const optionRecord = option as Record<string, unknown>
      const label = String(optionRecord.label ?? '').trim()
      const value = String(optionRecord.value ?? label).trim()
      const description = String(optionRecord.description ?? '').trim()
      if (!label || !value) return null
      return {
        label,
        value,
        ...(description ? { description } : {})
      }
    })
    .filter((option): option is NonNullable<typeof option> => option !== null)

  if (options.length === 0) return null

  const selectionMode =
    fields.selectionMode === 'MULTI_SELECT' || fields.selection_mode === 'MULTI_SELECT'
      ? 'MULTI_SELECT'
      : 'SINGLE_SELECT'
  const widgetId = String(fields.widgetId ?? fields.widget_id ?? '').trim()
  const defaultSelectedValuesRaw =
    fields.defaultSelectedValues ?? fields.default_selected_values ?? []
  const defaultSelectedValues = Array.isArray(defaultSelectedValuesRaw)
    ? defaultSelectedValuesRaw.map(value => String(value)).filter(Boolean)
    : []

  return {
    widgetId: widgetId || `selection-widget-${fallbackLabel || 'inline'}`,
    selectionMode,
    label: String(fields.label ?? fallbackLabel ?? '').trim(),
    options,
    defaultSelectedValues,
    placeholder: String(fields.placeholder ?? '').trim(),
    inputPrefix: String(fields.inputPrefix ?? fields.input_prefix ?? '')
  }
}

/** Maps persisted API messages to the streaming message shape used by ChatDisplay. */
export function convertApiMessagesToStreamingMessages(apiMessages: Message[]): StreamingMessage[] {
  const allMessages: StreamingMessage[] = []

  apiMessages.forEach((msg: Message, index: number) => {
    const msgId = msg.id
    const baseId = msgId ? `inline-${msgId}` : `api-${index}`

    if (msg.role === 'user') {
      allMessages.push({
        id: baseId,
        role: 'user',
        type: 'text',
        content: msg.text || '',
        contextUpload: msg.message_metadata?.context_upload ?? undefined,
        userName: msg.user_name || undefined,
        createdTime: msg.created_time || undefined
      })
      return
    }

    if (msg.blocks && msg.blocks.length > 0) {
      const canvasItems: CanvasItem[] = []

      msg.blocks.forEach((block, blockIndex: number) => {
        const blockId = `${baseId}-${blockIndex}`

        if (block.type === 'text' && block.text?.text) {
          allMessages.push({
            id: blockId,
            role: 'assistant',
            type: 'text',
            content: block.text.text,
            showFeedback: true,
            createdTime: msg.created_time || undefined
          })
        } else if (block.type === 'thinking') {
          const fields = block.fields as Record<string, unknown> | undefined
          const progressData: ProgressMessageData = {
            taskStatus: (fields?.taskStatus as string) || '',
            messageType: (fields?.messageType as string) || 'INTERNAL_PROGRESS',
            title: (fields?.title as string) || block.text?.text || '',
            internalMsgs: (fields?.internalMsgs as string[]) || [],
            toolOutputs: (fields?.toolOutputs as string[]) || [],
            checkpointMsg: (fields?.checkpointMsg as string) || '',
            msgHash: (fields?.msgHash as string) || blockId,
            createdTsNs: (fields?.createdTsNs as number) || 0,
            preview: (fields?.preview as string) || undefined,
            todoList: (fields?.todoList as ProgressMessageData['todoList']) || undefined
          }
          allMessages.push({
            id: blockId,
            role: 'assistant',
            type: 'progress_message',
            content: block.text?.text || '',
            progressMessage: progressData,
            createdTime: msg.created_time || undefined
          })
        } else if (block.type === 'task' && block.fields) {
          allMessages.push({
            id: blockId,
            role: 'assistant',
            type: 'task',
            content: '',
            taskId: block.fields.task_id as number,
            taskTitle: block.fields.title as string,
            createdTime: msg.created_time || undefined
          })
        } else if (block.type === 'image' && block.fields) {
          allMessages.push({
            id: blockId,
            role: 'assistant',
            type: 'image_base64',
            content: block.fields.base64 as string,
            createdTime: msg.created_time || undefined
          })
        } else if (block.type === 'canvas' && block.fields) {
          const fields = block.fields as Record<string, unknown>
          const rawCanvasType = (fields.canvas_type as string) || 'CANVAS_TYPE_UNKNOWN'
          const canvasType = (
            rawCanvasType === 'CANVAS_TYPE_UNKNOWN' ||
            rawCanvasType === 'CANVAS_TYPE_MARKDOWN' ||
            rawCanvasType === 'CANVAS_TYPE_IMAGE' ||
            rawCanvasType === 'CANVAS_TYPE_S3_FILE'
              ? rawCanvasType
              : 'CANVAS_TYPE_UNKNOWN'
          ) as CanvasItem['canvasType']
          canvasItems.push({
            canvasType,
            content: (fields.content as string) || '',
            s3FilePath: (fields.s3_file_path as string) || '',
            title: (fields.title as string) || block.text?.text || '',
            base64EncodedImage: (fields.base64_encoded_image as string) || '',
            editable: (fields.editable as boolean) || false
          })
        } else if (block.type === 'coder_summary' && block.text?.text) {
          const fields = block.fields as Record<string, unknown> | undefined
          allMessages.push({
            id: blockId,
            role: 'assistant',
            type: 'coder_summary',
            content: block.text.text,
            taskId: fields?.task_id as number | undefined,
            taskTitle: fields?.cmd_type as string | undefined,
            createdTime: msg.created_time || undefined
          })
        } else if (block.type === 'selection_widget' && block.fields) {
          const widget = normalizeSelectionWidgetFields(
            block.fields as Record<string, unknown>,
            block.text?.text ?? ''
          )
          if (widget) {
            allMessages.push({
              id: blockId,
              role: 'assistant',
              type: 'selection_widget',
              content: '',
              selectionWidget: widget,
              createdTime: msg.created_time || undefined
            })
          }
        }
      })

      if (canvasItems.length > 0) {
        allMessages.push({
          id: `${baseId}-canvas`,
          role: 'assistant',
          type: 'canvas',
          content: '',
          canvasItems,
          createdTime: msg.created_time || undefined
        })
      }
      return
    }

    if (msg.text) {
      allMessages.push({
        id: baseId,
        role: 'assistant',
        type: 'text',
        content: msg.text,
        feedback: msg.message_metadata?.feedback ?? undefined,
        feedbackReason: msg.message_metadata?.feedback_reason ?? undefined,
        otherReason: msg.message_metadata?.other_reason ?? undefined,
        showFeedback: true,
        createdTime: msg.created_time || undefined
      })
    }
  })

  return allMessages
}
